import { authFetch, authHeaders, resolveApiBaseUrl } from '../auth';
import { apiUrl } from './http';

export const ordersMethods = {
// Get all orders from database
  async getAllOrders(orderScope = 'shipping') {
    return this.getOrdersPage({ orderScope, limit: 50, offset: 0 });
  },

  async getOrdersPage({
    orderScope = 'shipping',
    limit = 50,
    offset = 0,
    status = 'all',
    statusPreset = 'none',
    orderType = 'all',
    paymentStatus = 'all',
    q = '',
    dateFrom = '',
    dateTo = '',
    preorderActive = false,
  } = {}) {
    try {
      const scope = ['all', 'shipping', 'digital'].includes(orderScope) ? orderScope : 'shipping';
      const base = resolveApiBaseUrl();
      const params = new URLSearchParams();
      params.set('order_scope', scope);
      params.set('limit', String(limit));
      params.set('offset', String(offset || 0));
      if (status && status !== 'all') params.set('status', status);
      if (statusPreset && statusPreset !== 'none') params.set('status_preset', statusPreset);
      if (orderType && orderType !== 'all') params.set('order_type', orderType);
      if (paymentStatus && paymentStatus !== 'all') params.set('payment_status', paymentStatus);
      if (q && String(q).trim()) params.set('q', String(q).trim());
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (preorderActive) params.set('preorder_active', '1');
      const url = `${apiUrl(base, 'orders/')}?${params.toString()}`;
      const response = await authFetch(url, {
        method: 'GET',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      return {
        success: result.success !== false,
        orders: result.orders || [],
        total: result.total || 0,
        next_offset: result.next_offset ?? null,
        status_counts: result.status_counts || {},
        filter_applied: result.filter_applied || scope,
        error: result.error,
      };
    } catch (error) {
      console.error('Failed to fetch orders from database:', error);
      return {
        success: false,
        error: error.message,
        orders: [],
        total: 0,
        next_offset: null,
        status_counts: {},
      };
    }
  },

  async downloadOrdersExport({
    orderScope = 'shipping',
    status = 'all',
    statusPreset = 'none',
    orderType = 'all',
    paymentStatus = 'all',
    q = '',
    dateFrom = '',
    dateTo = '',
    preorderActive = false,
    format = 'csv',
  } = {}) {
    try {
      const scope = ['all', 'shipping', 'digital'].includes(orderScope) ? orderScope : 'shipping';
      const fmt = format === 'excel' || format === 'xlsx' ? 'xlsx' : 'csv';
      const params = new URLSearchParams();
      params.set('order_scope', scope);
      params.set('status', status || 'all');
      params.set('status_preset', statusPreset || 'none');
      params.set('order_type', orderType || 'all');
      params.set('payment_status', paymentStatus || 'all');
      if (q && String(q).trim()) params.set('q', String(q).trim());
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (preorderActive) params.set('preorder_active', '1');
      params.set('format', fmt);
      const base = resolveApiBaseUrl();
      const url = `${apiUrl(base, 'orders/export/')}?${params.toString()}`;
      const response = await authFetch(url, { method: 'GET' });
      if (!response.ok) {
        throw new Error(`Export failed (${response.status})`);
      }
      const blob = await response.blob();
      const stamp = new Date().toISOString().split('T')[0];
      const filename = `orders-export-${stamp}.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`;
      return { success: true, blob, filename };
    } catch (error) {
      return { success: false, error: error.message, blob: null, filename: null };
    }
  },

  async getOrderDashboardDetail(orderId) {
    try {
      const result = await this.makeAPICall(`/orders/${orderId}/dashboard/`);
      return {
        success: result.success !== false,
        order: result.order || null,
        error: result.error,
      };
    } catch (error) {
      return { success: false, error: error.message, order: null };
    }
  },

  async getOrdersDashboardDetails(orderIds = []) {
    const ids = (orderIds || []).filter((id) => id != null && id !== '');
    if (!ids.length) {
      return { success: true, orders: [] };
    }
    try {
      const params = new URLSearchParams();
      params.set('ids', ids.slice(0, 100).join(','));
      const result = await this.makeAPICall(`/orders/dashboard-details/?${params.toString()}`);
      return {
        success: result.success !== false,
        orders: result.orders || [],
        error: result.error,
      };
    } catch (error) {
      return { success: false, error: error.message, orders: [] };
    }
  },

  async getOrdersTracking({
    q = '',
    status = '',
    orderScope = 'all',
    book = '',
    dateFrom = '',
    dateTo = '',
    limit = 30,
    offset = null,
  } = {}) {
    try {
      const scope = ['all', 'shipping', 'digital'].includes(orderScope) ? orderScope : 'all';
      const params = new URLSearchParams();
      if (q && String(q).trim()) params.set('q', String(q).trim());
      if (status && String(status).trim()) params.set('status', String(status).trim());
      if (book && String(book).trim()) params.set('book', String(book).trim());
      if (dateFrom && String(dateFrom).trim()) params.set('date_from', String(dateFrom).trim());
      if (dateTo && String(dateTo).trim()) params.set('date_to', String(dateTo).trim());
      params.set('order_scope', scope);
      params.set('limit', String(limit));
      if (offset !== null && offset !== undefined && offset !== '') {
        params.set('offset', String(offset));
      }
      const base = resolveApiBaseUrl();
      const url = `${apiUrl(base, 'orders/tracking/')}?${params.toString()}`;
      const response = await authFetch(url, {
        method: 'GET',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      return {
        success: result.success !== false,
        orders: result.orders || [],
        next_offset: result.next_offset || null,
        status_options: result.status_options || [],
        book_options: result.book_options || [],
        status_counts: result.status_counts || [],
        total_count: result.total_count || 0,
        error: result.error,
      };
    } catch (error) {
      console.error('Failed to fetch order tracking:', error);
      return {
        success: false,
        error: error.message,
        orders: [],
        next_offset: null,
        status_options: [],
        book_options: [],
        status_counts: [],
        total_count: 0,
      };
    }
  },

  /**
   * Server-side streaming export — does not load all rows into browser memory.
   * format: 'csv' | 'xlsx'
   */
  async downloadTrackingExport({
    q = '',
    status = '',
    orderScope = 'all',
    book = '',
    dateFrom = '',
    dateTo = '',
    format = 'csv',
  } = {}) {
    try {
      const scope = ['all', 'shipping', 'digital'].includes(orderScope) ? orderScope : 'all';
      const fmt = format === 'excel' || format === 'xlsx' ? 'xlsx' : 'csv';
      const params = new URLSearchParams();
      if (q && String(q).trim()) params.set('q', String(q).trim());
      if (status && String(status).trim()) params.set('status', String(status).trim());
      if (book && String(book).trim()) params.set('book', String(book).trim());
      if (dateFrom && String(dateFrom).trim()) params.set('date_from', String(dateFrom).trim());
      if (dateTo && String(dateTo).trim()) params.set('date_to', String(dateTo).trim());
      params.set('order_scope', scope);
      params.set('format', fmt);
      const base = resolveApiBaseUrl();
      const url = `${apiUrl(base, 'orders/tracking/export/')}?${params.toString()}`;
      const response = await authFetch(url, {
        method: 'GET',
        headers: authHeaders(),
      });
      if (!response.ok) {
        let err = `Export failed (${response.status})`;
        try {
          const data = await response.json();
          err = data.error || err;
        } catch (_) {
          // binary error body
        }
        throw new Error(err);
      }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename =
        (match && match[1]) ||
        `order-tracking-export.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`;
      return { success: true, blob, filename };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Create a new order in database (manual order)
  async createOrder(orderData) {
    try {
      // Create in Shopify, backend will sync to DB automatically
      const result = await this.makeAPICall('/orders/create/', 'POST', orderData);
      return {
        success: result.success === true,
        message: result.message,
        order_id: result.order_id,
        order_number: result.order_number,
        initial_status: result.initial_status
      };
    } catch (error) {
      console.error('Failed to create order:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Create order in Shopify and sync to DB
  async createOrderInShopify(orderData) {
    try {
      const result = await this.makeAPICall('/orders/create/', 'POST', orderData);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Sync orders from Shopify to database
  async syncOrdersFromShopify() {
    try {
      const result = await this.makeAPICall('/orders/sync/', 'POST');
      return {
        success: true,
        message: result.message,
        synced: result.synced,
        updated: result.updated,
        total: result.total
      };
    } catch (error) {
      console.error('Failed to sync orders from Shopify:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // ⚡ FAST Incremental Sync - Only new/updated orders
  async fastSyncOrdersFromShopify() {
    try {
      const result = await this.makeAPICall('/orders/fast-sync/', 'POST');
      return {
        success: true,
        message: result.message,
        synced: result.synced,
        updated: result.updated,
        total_processed: result.total_processed,
        method: result.method,
        performance: result.performance
      };
    } catch (error) {
      console.error('Failed to fast sync orders from Shopify:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Update order status in database
  async updateOrderStatus(orderId, status, trackingNumber = '', notes = '') {
    try {
      const result = await this.makeAPICall('/orders/update-status/', 'POST', {
        order_id: orderId,
        status: status,
        tracking_number: trackingNumber,
        notes: notes
      });

      return {
        success: true,
        message: result.message,
        order_id: result.order_id,
        old_status: result.old_status,
        new_status: result.new_status
      };
    } catch (error) {
      console.error('Failed to update order status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Get order history
  async getOrderHistory(orderId) {
    try {
      const result = await this.makeAPICall(`/orders/${orderId}/history/`);
      return {
        success: true,
        history: result.history || []
      };
    } catch (error) {
      console.error('Failed to fetch order history:', error);
      return {
        success: false,
        error: error.message,
        history: []
      };
    }
  },

  getInvoiceDownloadUrl(orderId) {
    const base = resolveApiBaseUrl();
    return apiUrl(base, `invoices/${orderId}/download/`);
  },

  async downloadInvoice(orderId, invoiceUrl = null) {
    try {
      const url = invoiceUrl || this.getInvoiceDownloadUrl(orderId);
      const response = await authFetch(url, { method: 'GET', redirect: 'follow', headers: authHeaders() });
      if (!response.ok) {
        throw new Error(`Failed to download invoice (${response.status})`);
      }
      const blob = await response.blob();
      return { success: true, blob, url };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  async downloadBulkInvoices(dateFrom, dateTo, orderScope = 'shipping') {
    try {
      const base = resolveApiBaseUrl();
      const params = new URLSearchParams();
      params.set('date_from', dateFrom);
      params.set('date_to', dateTo);
      params.set('order_scope', orderScope);
      const url = `${apiUrl(base, 'invoices/bulk/download/')}?${params.toString()}`;
      const response = await authFetch(url, { method: 'GET', headers: authHeaders() });
      if (!response.ok) {
        let err = `Failed to download bulk invoices (${response.status})`;
        try {
          const data = await response.json();
          err = data.error || err;
        } catch (_) {
          // no-op
        }
        throw new Error(err);
      }
      const blob = await response.blob();
      return { success: true, blob };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};
